import { UserRole } from '@features/settings/types/settings.types';
import { useProfile } from '@shared/hooks/useProfile';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../../config/firebase';
import { SUPPORT_TICKETS_COLLECTION } from '../constants/collections';
import { SupportTicket } from '../types';

/**
 * Hook to manage support tickets
 * Different behavior based on user role (admin or student)
 */
export const useSupportTickets = () => {
  const { user } = useProfile();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // Using centralized collection name from constants

  // Get all tickets if admin, or only user's tickets if student
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const isAdmin = user.role === UserRole.ADMIN;

    // Query tickets collection
    let ticketsQuery = query(
      collection(db, SUPPORT_TICKETS_COLLECTION),
      orderBy('updatedAt', 'desc')
    );

    // If user is not admin, only show their tickets
    if (!isAdmin) {
      ticketsQuery = query(
        collection(db, SUPPORT_TICKETS_COLLECTION),
        where('userId', '==', user.id),
        orderBy('updatedAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(
      ticketsQuery,
      (snapshot) => {
        const supportTickets = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          lastMessage: doc.data().lastMessage
            ? {
                ...doc.data().lastMessage,
                timestamp: doc.data().lastMessage.timestamp?.toDate(),
              }
            : undefined,
        })) as SupportTicket[];

        setTickets(supportTickets);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading support tickets:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Create a new support ticket
  const createTicket = useCallback(async () => {
    if (!user) return null;

    try {
      // First check if user already has an open ticket
      const existingTicket = tickets.find(
        (ticket) => ticket.userId === user.id && ticket.status === 'open'
      );

      if (existingTicket) {
        setSelectedTicketId(existingTicket.id);
        return existingTicket.id;
      }

      // Create a new ticket
      const ticketData: Omit<SupportTicket, 'id'> = {
        userId: user.id,
        status: 'open',
        createdAt: new Date(),
        updatedAt: new Date(),
        unreadCount: 0,
        unreadAdminCount: 0,
        unreadUserCount: 0,
      };

      // Use user ID as ticket ID to ensure one open ticket per user
      const ticketId = `ticket_${user.id}`;
      const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);

      await setDoc(ticketRef, {
        ...ticketData,
        createdAt: Timestamp.fromDate(ticketData.createdAt),
        updatedAt: Timestamp.fromDate(ticketData.updatedAt),
      });

      setSelectedTicketId(ticketId);
      return ticketId;
    } catch (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }
  }, [user, tickets]);

  // Close a support ticket
  const closeTicket = useCallback(
    async (ticketId: string) => {
      try {
        const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);
        await updateDoc(ticketRef, {
          status: 'closed',
          updatedAt: Timestamp.now(),
        });

        // If the closed ticket was selected, deselect it
        if (selectedTicketId === ticketId) {
          setSelectedTicketId(null);
        }
      } catch (error) {
        console.error('Error closing support ticket:', error);
        throw error;
      }
    },
    [selectedTicketId]
  );

  // Reopen a support ticket
  const reopenTicket = useCallback(async (ticketId: string) => {
    try {
      const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);
      await updateDoc(ticketRef, {
        status: 'open',
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error reopening support ticket:', error);
      throw error;
    }
  }, []);

  // Get the total number of unread messages
  const getTotalUnreadCount = useCallback(() => {
    if (!user) return 0;

    const isAdmin = user.role === UserRole.ADMIN;

    return tickets.reduce((total, ticket) => {
      // For admins, count unreadAdminCount, for users count unreadUserCount
      const unreadCount = isAdmin
        ? ticket.unreadAdminCount
        : ticket.unreadUserCount;
      return total + (unreadCount || 0);
    }, 0);
  }, [tickets, user]);

  return {
    tickets,
    loading,
    error,
    selectedTicketId,
    setSelectedTicketId,
    createTicket,
    closeTicket,
    reopenTicket,
    getTotalUnreadCount,
  };
};
